import {
    Button,
    HStack,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Alert, ServiceCheckbox } from '@acp/core-ui';
import { ServiceIdMapper } from '@acp/shared';
import { RequestCancellationFormValues } from './form-schema';
import { PropertyAddressCard } from '../property-address-card';
import { getCancellationRequestSummary } from '../../service';
import { FinancialObligationConfirmationModal } from './financial-obligation-confirmation-modal';

function ConfirmCancellationScreen({
    propertyId,
    onBack,
    onSubmit,
    isLoading,
    error,
}: {
    propertyId: number;
    onSubmit: (vals: RequestCancellationFormValues) => void;
    onBack: () => void;
    isLoading: boolean;
    error: Error | null;
}) {
    const form = useFormContext<RequestCancellationFormValues>();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const cancellationSummaryQuery = useQuery({
        queryKey: ['property', propertyId, 'cancellation-request-summary'],
        queryFn: () => getCancellationRequestSummary(propertyId),
    });

    const serviceIds = form.watch('services');
    const financialObligation = cancellationSummaryQuery.data?.financialObligation;

    const handleNextStep = () => {
        const isOutstanding = financialObligation?.isOutstanding === true;
        const hasAmount = (financialObligation?.finalBalanceAmount ?? 0) > 0;

        if (isOutstanding && hasAmount) {
            onOpen();
        } else {
            void form.handleSubmit(onSubmit)();
        }
    };

    return (
        <>
            <ModalHeader>
                Confirm service cancellation
            </ModalHeader>
            <ModalBody>
                <PropertyAddressCard w="full" mb={5} propertyId={propertyId} />

                <Text fontWeight="medium" fontSize="md" mb={2}>Services to be cancelled</Text>
                {
                    cancellationSummaryQuery.data !== undefined && (
                        <HStack gap={2} w="full" flexWrap="wrap" mb={5}>
                            {
                                cancellationSummaryQuery.data
                                    .cancellableServices
                                    .filter((x) => serviceIds.includes(x.id))
                                    .map((service) => (
                                        <ServiceCheckbox
                                            key={service.id}
                                            serviceName={
                                                ServiceIdMapper.getFormattedServiceName(
                                                    service.shortName
                                                )
                                            }
                                            onChange={() => {}}
                                            isChecked={false}
                                            hideCheckbox
                                        />
                                    ))
                            }
                        </HStack>
                    )
                }

                <Text fontWeight="bold" fontSize="lg" mb={2}>What happens next?</Text>

                <Text>
                    We will review your request and process the cancellation within 48 hours.
                    You will be notified once it is complete.
                </Text>

                {
                    error !== null && (
                        <Alert
                            status="error"
                            title="An error occurred while submitting your cancellation request. Please try again."
                            mt={5}
                        >
                            {error.message}
                        </Alert>
                    )
                }

                <FinancialObligationConfirmationModal
                    isOpen={isOpen}
                    onClose={onClose}
                    amount={financialObligation?.finalBalanceAmountFormatted ?? ''}
                    onConfirm={form.handleSubmit(onSubmit)}
                />
            </ModalBody>

            <ModalFooter justifyContent="space-between">
                <Button
                    colorScheme="muted"
                    variant="ghost"
                    onClick={onBack}
                >
                    Back
                </Button>
                <Button
                    colorScheme="secondary"
                    onClick={handleNextStep}
                    isLoading={isLoading}
                >
                    Request cancellation
                </Button>
            </ModalFooter>
        </>
    );
}

export { ConfirmCancellationScreen };
